import React from "react";
import styled from "styled-components";
import { Container, Eyebrow, Display } from "../landing/ui";
import { colors, fonts, media } from "../../styles/theme";
import { ndis } from "../../pages/ndisContent";
import { contact } from "../../pages/landingContent";

// The two NDIS-only parts of the vertical template. `NdisStory` sits where the other pages
// carry their testimonials; `NdisPlan` carries the plan, line item, inclusions, suitability
// and next steps, below the evidence. Wording rules live with the copy in ndisContent.js.

const Band = styled.section`
  padding: 0 0 120px;
`;

const Head = styled.div`
  margin-bottom: 40px;
`;

const Sub = styled.h3`
  margin-bottom: 24px;
  font-family: ${fonts.display};
  font-weight: 300;
  font-size: clamp(28px, 3vw, 40px);
  letter-spacing: -0.03em;
  color: ${colors.ink};
`;

const Group = styled.div`
  & + & {
    margin-top: 72px;
  }
`;

// One participant's own words, kept visibly separate from anything vimbal claims. Left: who
// he is and what he said. Right: what his family saw afterwards, as a short timeline, so the
// column carries the evidence over time instead of three loose bullets.
const Story = styled.div`
  position: relative;
  display: grid;
  gap: 36px;
  padding: 40px 28px;
  border-radius: 28px;
  background: ${colors.forest};
  color: ${colors.linen};
  overflow: hidden;

  @media ${media.medium} {
    grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
    gap: 56px;
    padding: 56px 56px 44px;
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(50% 60% at 100% 0%, rgba(184, 115, 51, 0.18), transparent 70%);
    pointer-events: none;
  }
  > * {
    position: relative;
  }

  .tag {
    display: inline-block;
    margin-bottom: 18px;
    padding: 6px 12px;
    border-radius: 999px;
    border: 1px solid rgba(250, 247, 241, 0.25);
    font-family: ${fonts.mono};
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${colors.sage};
  }
  .who b {
    display: block;
    font-family: ${fonts.display};
    font-weight: 400;
    font-size: clamp(22px, 2.2vw, 30px);
    line-height: 1.15;
    letter-spacing: -0.02em;
  }
  .who span {
    display: block;
    margin-top: 8px;
    font-family: ${fonts.body};
    font-size: 15px;
    color: rgba(250, 247, 241, 0.65);
  }
  .quotes {
    margin-top: 32px;
    padding-top: 28px;
    border-top: 1px solid rgba(250, 247, 241, 0.14);
  }
  blockquote {
    position: relative;
    padding-left: 22px;
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: clamp(20px, 2vw, 26px);
    line-height: 1.35;
    letter-spacing: -0.01em;
  }
  blockquote::before {
    content: "";
    position: absolute;
    left: 0;
    top: 6px;
    bottom: 6px;
    width: 2px;
    border-radius: 2px;
    background: ${colors.copperLight};
  }
  blockquote + blockquote {
    margin-top: 22px;
  }

  .after {
    align-self: start;
    padding: 28px 28px 8px;
    border-radius: 22px;
    background: rgba(250, 247, 241, 0.05);
    border: 1px solid rgba(250, 247, 241, 0.1);
  }
  .after > b {
    display: block;
    margin-bottom: 22px;
    font-family: ${fonts.mono};
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${colors.sage};
  }
  ol {
    list-style: none;
    position: relative;
    padding-left: 26px;
  }
  ol::before {
    content: "";
    position: absolute;
    left: 5px;
    top: 8px;
    bottom: 24px;
    width: 1px;
    background: rgba(250, 247, 241, 0.2);
  }
  li {
    position: relative;
    padding-bottom: 26px;
  }
  li::before {
    content: "";
    position: absolute;
    left: -26px;
    top: 5px;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: ${colors.copperLight};
    box-shadow: 0 0 0 4px ${colors.forest};
  }
  li span {
    display: block;
    margin-bottom: 6px;
    font-family: ${fonts.mono};
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${colors.copperLight};
  }
  li p {
    font-family: ${fonts.body};
    font-size: 16px;
    line-height: 1.55;
    color: rgba(250, 247, 241, 0.88);
  }
  .disclaimer {
    grid-column: 1 / -1;
    margin-top: 4px;
    font-family: ${fonts.body};
    font-size: 13px;
    line-height: 1.6;
    color: rgba(250, 247, 241, 0.5);
  }
`;

// The second participant, lighter so the two stories don't compete.
const Second = styled.div`
  display: grid;
  gap: 20px;
  margin-top: 16px;
  padding: 30px 26px;
  border-radius: 26px;
  background: ${colors.linen};
  border: 1px solid ${colors.stone};

  @media ${media.medium} {
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
    gap: 40px;
    padding: 36px 40px;
  }

  .who b {
    display: block;
    font-family: ${fonts.display};
    font-weight: 400;
    font-size: clamp(20px, 2vw, 24px);
    letter-spacing: -0.02em;
    color: ${colors.ink};
  }
  .who span {
    display: block;
    margin-top: 6px;
    font-family: ${fonts.body};
    font-size: 15px;
    color: ${colors.pebble};
  }
  blockquote {
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: clamp(18px, 1.8vw, 22px);
    line-height: 1.4;
    color: ${colors.ink};
  }
  blockquote + blockquote {
    margin-top: 14px;
  }
`;

/* ---------- problem to answer, left to right (same shape as the founder page) ---------- */

const PairLabels = styled.div`
  display: none;

  @media ${media.medium} {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 64px minmax(0, 1fr);
    margin-bottom: 12px;
    font-family: ${fonts.mono};
    font-size: 12px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }
  span:first-child {
    color: ${colors.terracotta};
  }
  span:last-child {
    grid-column: 3;
    color: ${colors.moss};
  }
`;

const Pair = styled.div`
  display: grid;
  gap: 10px;
  align-items: stretch;
  margin-bottom: 12px;

  @media ${media.medium} {
    grid-template-columns: minmax(0, 1fr) 64px minmax(0, 1fr);
    gap: 0;
  }

  p {
    padding: 22px 24px;
    border-radius: 20px;
    font-family: ${fonts.display};
    font-size: clamp(20px, 1.8vw, 24px);
    line-height: 1.25;
    letter-spacing: -0.01em;
  }
  p.problem {
    background: ${colors.sand};
    color: ${colors.bark};
  }
  p.answer {
    background: ${colors.forest};
    color: ${colors.linen};
  }
  small {
    display: block;
    margin-bottom: 6px;
    font-family: ${fonts.mono};
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    opacity: 0.7;

    @media ${media.medium} {
      display: none;
    }
  }
  i {
    display: none;

    @media ${media.medium} {
      display: grid;
      place-items: center;
      font-style: normal;
      font-size: 22px;
      color: ${colors.copper};
    }
  }
`;

const PairsNote = styled.p`
  margin-top: 20px;
  max-width: 760px;
  font-family: ${fonts.body};
  font-size: 14px;
  line-height: 1.6;
  color: ${colors.pebble};
`;

export const NdisPairs = ({ labels, pairs, note }) => (
  <>
    <PairLabels aria-hidden="true">
      <span>{labels.left}</span>
      <span>{labels.right}</span>
    </PairLabels>
    {pairs.map((pr, i) => (
      <Pair key={pr.problem} data-reveal={i * 0.05}>
        <p className="problem">
          <small>{labels.left}</small>
          {pr.problem}
        </p>
        <i aria-hidden="true">→</i>
        <p className="answer">
          <small>{labels.right}</small>
          {pr.answer}
        </p>
      </Pair>
    ))}
    {note && <PairsNote>{note}</PairsNote>}
  </>
);

export const NdisStory = () => (
  <Band aria-labelledby="v-story">
    <Container>
      <Head data-reveal>
        <Eyebrow $color={colors.moss}>In their words</Eyebrow>
        <Display id="v-story" style={{ marginTop: 18 }}>
          What it felt like, <em>in their own words.</em>
        </Display>
      </Head>
      <Story data-reveal="0.05">
        <div>
          <span className="tag">NDIS participant</span>
          <div className="who">
            <b>{ndis.story.who}</b>
            <span>{ndis.story.detail}</span>
          </div>
          <div className="quotes">
            {ndis.story.quotes.map((q) => (
              <blockquote key={q}>“{q}”</blockquote>
            ))}
          </div>
        </div>
        <div className="after">
          <b>{ndis.story.afterTitle}</b>
          <ol>
            {ndis.story.after.map((a) => (
              <li key={a.when}>
                <span>{a.when}</span>
                <p>{a.what}</p>
              </li>
            ))}
          </ol>
        </div>
        <p className="disclaimer">{ndis.story.disclaimer}</p>
      </Story>
      <Second data-reveal="0.1">
        <div className="who">
          <b>{ndis.second.who}</b>
          <span>{ndis.second.detail}</span>
        </div>
        <div>
          {ndis.second.quotes.map((q) => (
            <blockquote key={q}>“{q}”</blockquote>
          ))}
        </div>
      </Second>
    </Container>
  </Band>
);

// The funding panel: the part coordinators and plan managers read first.
const PlanCard = styled.div`
  display: grid;
  gap: 18px;
  padding: 30px 26px;
  border-radius: 26px;
  background: ${colors.linen};
  border: 1px solid ${colors.stone};

  @media ${media.medium} {
    padding: 38px 40px;
  }

  .status {
    font-family: ${fonts.display};
    font-size: clamp(20px, 2vw, 26px);
    letter-spacing: -0.02em;
    color: ${colors.ink};
  }
  .managed {
    font-family: ${fonts.body};
    font-size: 17px;
    line-height: 1.6;
    color: ${colors.bark};
  }
  .line-item {
    padding: 18px 20px;
    border-radius: 18px;
    background: rgba(163, 177, 143, 0.18);
  }
  .line-item b {
    display: block;
    font-family: ${fonts.mono};
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${colors.moss};
  }
  .line-item strong {
    display: block;
    margin-top: 8px;
    font-family: ${fonts.body};
    font-weight: 600;
    font-size: 16px;
    color: ${colors.ink};
  }
  .line-item code {
    display: inline-block;
    margin-top: 6px;
    font-family: ${fonts.mono};
    font-size: 13px;
    color: ${colors.bark};
  }
  .line-item span {
    display: block;
    margin-top: 6px;
    font-family: ${fonts.body};
    font-size: 15px;
    color: ${colors.bark};
  }
  .caution {
    font-family: ${fonts.body};
    font-size: 14px;
    line-height: 1.6;
    color: ${colors.pebble};
  }
`;

const Cards = styled.div`
  display: grid;
  gap: 14px;

  @media ${media.small} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media ${media.large} {
    grid-template-columns: ${(p) => (p.$three ? "repeat(3, minmax(0, 1fr))" : "repeat(5, minmax(0, 1fr))")};
  }
`;

const Card = styled.div`
  padding: 24px 22px;
  border-radius: 22px;
  background: ${colors.linen};
  border: 1px solid ${colors.stone};

  b {
    display: block;
    font-family: ${fonts.display};
    font-weight: 400;
    font-size: 19px;
    letter-spacing: -0.01em;
    color: ${colors.ink};
  }
  span {
    display: block;
    margin-top: 8px;
    font-family: ${fonts.body};
    font-size: 15px;
    line-height: 1.5;
    color: ${colors.bark};
  }
  em {
    display: block;
    margin-bottom: 10px;
    font-family: ${fonts.mono};
    font-size: 11px;
    letter-spacing: 0.14em;
    color: ${colors.copper};
  }
`;

const Checks = styled.ul`
  list-style: none;
  display: grid;
  gap: 12px;
  max-width: 820px;

  li {
    position: relative;
    padding-left: 26px;
    font-family: ${fonts.body};
    font-size: 17px;
    line-height: 1.6;
    color: ${colors.bark};
  }
  li::before {
    content: "";
    position: absolute;
    left: 0;
    top: 9px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 2px solid ${colors.moss};
  }
`;

const ContactRow = styled.div`
  display: grid;
  gap: 16px;
  padding: 30px 26px;
  border-radius: 24px;
  background: rgba(163, 177, 143, 0.2);

  @media ${media.medium} {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    padding: 34px 40px;
  }

  p {
    font-family: ${fonts.body};
    font-size: 17px;
    line-height: 1.6;
    color: ${colors.bark};
    max-width: 620px;
  }
  .links {
    display: grid;
    gap: 6px;
    font-family: ${fonts.body};
    font-size: 16px;
  }
  a {
    color: ${colors.copper};
    font-weight: 600;
  }
`;

const Steps = styled.ol`
  list-style: none;
  display: grid;
  gap: 14px;

  @media ${media.medium} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

export const NdisPlan = () => (
  <Band aria-labelledby="v-plan">
    <Container>
      <Head data-reveal>
        <Eyebrow $color={colors.copper}>Under your plan</Eyebrow>
        <Display id="v-plan" style={{ marginTop: 18 }}>
          {ndis.plan.title}
        </Display>
      </Head>
      <Group>
        <PlanCard data-reveal="0.05">
          <p className="status">{ndis.plan.status}</p>
          <p className="managed">{ndis.plan.managed}</p>
          <div className="line-item">
            <b>{ndis.plan.lineItemLabel}</b>
            <strong>{ndis.plan.lineItem}</strong>
            <code>{ndis.plan.lineItemCode}</code>
            <span>{ndis.plan.lineItemNote}</span>
          </div>
          <p className="caution">{ndis.plan.caution}</p>
        </PlanCard>
      </Group>

      <Group>
        <Sub data-reveal>{ndis.included.title}</Sub>
        <Cards>
          {ndis.included.items.map((item, i) => (
            <Card key={item.label} data-reveal={i * 0.05}>
              <b>{item.label}</b>
              <span>{item.body}</span>
            </Card>
          ))}
        </Cards>
      </Group>

      <Group>
        <Sub data-reveal>{ndis.suitability.title}</Sub>
        <Checks data-reveal="0.05">
          {ndis.suitability.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </Checks>
      </Group>

      <Group>
        <Sub data-reveal>{ndis.coordinators.title}</Sub>
        <ContactRow data-reveal="0.05">
          <p>{ndis.coordinators.body}</p>
          <div className="links">
            <strong>{ndis.coordinators.cta}</strong>
            <a href={`mailto:${contact.email}`}>{contact.email}</a>
            <a href={`tel:${contact.phone.replace(/\s/g, "")}`}>{contact.phone}</a>
          </div>
        </ContactRow>
      </Group>

      <Group>
        <Sub data-reveal>{ndis.steps.title}</Sub>
        <Steps>
          {ndis.steps.items.map((step, i) => (
            <Card as="li" key={step.n} data-reveal={i * 0.05}>
              <em>{step.n}</em>
              <b>{step.label}</b>
              <span>{step.body}</span>
            </Card>
          ))}
        </Steps>
      </Group>
    </Container>
  </Band>
);
